import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';

const maxPollingTime = 5 * 60 * 1000; // Tiempo máximo de polling: 5 minutos
const pollingInterval = 5000; // Intervalo entre consultas: 5 segundos

function ProcessingStatus({
                              jobId,
                              status,
                              setStatus,
                              setSharePointUrl,
                              loading,
                              setLoading,
                              error,
                              setError,
                          }) {
    const pollIntervalRef = useRef(null);
    const startTimeRef = useRef(null);
    const [downloadUrl, setDownloadUrl] = useState(null);

    useEffect(() => {
        const checkStatus = async () => {
            try {
                setLoading(true);

                const response = await axios.get(
                    `https://macc-apimanagement-cloudservices-pro.azure-api.net/alquizen-docs/api/documents/status/${jobId}`,
                    {
                        timeout: 60000, // Tiempo de espera de 5 segundos por solicitud
                    }
                );

                const jobStatus = response.data;

                setStatus(jobStatus.status);

                if (jobStatus.status === 'completed') {
                    // Nuevo flujo: el backend devuelve un SAS en download_url.
                    setDownloadUrl(jobStatus.download_url || null);
                    // Ya no navegamos a SuccessView basado en SharePoint, por lo que no seteamos setSharePointUrl.
                    clearInterval(pollIntervalRef.current);
                } else if (jobStatus.status === 'failed') {
                    setError(jobStatus.message || 'Ha ocurrido un error al procesar los documentos.');
                    clearInterval(pollIntervalRef.current);
                }
            } catch (error) {
                console.error('Error al verificar el estado:', error);

                // Manejo específico del timeout de Axios
                if (error.code === 'ECONNABORTED') {
                    setStatus('timeout'); // Cambiar el estado a 'timeout'
                } else {
                    setError('Error al verificar el estado del proceso. Por favor, inténtalo de nuevo.');
                    clearInterval(pollIntervalRef.current);
                }
            } finally {
                setLoading(false);
            }
        };

        startTimeRef.current = Date.now();
        checkStatus();

        pollIntervalRef.current = setInterval(() => {
            if (Date.now() - startTimeRef.current > maxPollingTime) {
                clearInterval(pollIntervalRef.current);
                setError(null); // Limpiar cualquier error previo
                setStatus('timeout'); // Cambiar el estado a 'timeout'
                setLoading(false);
            } else {
                checkStatus();
            }
        }, pollingInterval);

        return () => {
            if (pollIntervalRef.current) {
                clearInterval(pollIntervalRef.current);
            }
        };
    }, [jobId, setStatus, setSharePointUrl, setLoading, setError]);

    return (
        <div className="text-center">
            <h4 className="mb-4">Procesando Documentos</h4>

            <div className="mb-4">
                {loading ? (
                    <div className="spinner-border text-primary mx-auto" role="status">
                        <span className="visually-hidden">Cargando...</span>
                    </div>
                ) : status === 'completed' ? (
                    <div className="text-success display-1 mb-2">✓</div>
                ) : status === 'timeout' ? ( // Manejo del estado timeout
                    <>
                        <div className="text-warning display-1 mb-2">⏳</div>
                        <p className="lead mt-3">
                            El volumen de documentos solicitados es alto. Puedes esperar a que finalice o verificar el estado más tarde usando el siguiente ID.
                        </p>
                        <button
                            className="btn btn-outline-primary mt-3"
                            onClick={() => navigator.clipboard.writeText(jobId)}
                        >
                            Copiar ID de Trabajo
                        </button>
                        <p className="mt-3">
                            <a
                                href={`/check-job-status?jobId=${jobId}`}
                                className="btn btn-primary"
                                role="button"
                            >
                                Verificar Estado Más Tarde
                            </a>
                        </p>
                    </>
                ) : error ? (
                    <>
                        <div className="text-danger display-1 mb-2">✗</div>
                        <p className="lead mt-3">
                            {error} <br />
                            <strong>ID de trabajo:</strong> {jobId}
                        </p>
                        <button
                            className="btn btn-outline-primary mt-3"
                            onClick={() => navigator.clipboard.writeText(jobId)}
                        >
                            Copiar ID de Trabajo
                        </button>
                        <p className="mt-3">
                            <a
                                href={`/check-job-status?jobId=${jobId}`}
                                className="btn btn-primary"
                                role="button"
                            >
                                Verificar Estado Más Tarde
                            </a>
                        </p>
                    </>
                ) : (
                    <div className="spinner-border text-primary mx-auto" role="status">
                        <span className="visually-hidden">Cargando...</span>
                    </div>
                )}

                <p className="lead mt-3">
                    {loading
                        ? 'Verificando estado...'
                        : status === 'completed'
                            ? 'Proceso completado con éxito!'
                            : status === 'processing'
                                ? 'Procesando documentos...'
                                : status === 'started'
                                    ? 'Iniciando proceso...'
                                    : status === 'timeout'
                                        ? 'El proceso está tardando más de lo esperado.'
                                        : ''}
                </p>

                <p className="text-muted small">
                    {status === 'completed' && downloadUrl ? (
                        <a href={downloadUrl} target="_blank" rel="noopener noreferrer">Descargar Documento</a>
                    ) : (
                        <>ID de trabajo: {jobId}</>
                    )}
                </p>
            </div>

            {error && status !== 'timeout' && (
                <div className="alert alert-danger" role="alert">
                    {error}
                </div>
            )}
        </div>
    );
}

export default ProcessingStatus;